/**
 * Compliance — institution's own CAP status.
 *
 * A SUSPENDED institution can submit a Corrective Action Plan from here;
 * an APPROVED institution sees a green posture badge.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function Compliance() {
  const { user } = useAuth();
  const [detail, setDetail] = useState(null);
  const [events, setEvents] = useState([]);
  const [planText, setPlanText] = useState("");
  const [busy, setBusy] = useState(false);
  const instId = user?.institution_id;

  const load = () => {
    if (!instId) return;
    Promise.all([
      api.get(`/cap/institution/${instId}`),
      api.get(`/cap/institution/${instId}/events?limit=50`),
    ]).then(([c, e]) => {
      setDetail(c.data);
      setEvents(e.data?.items || []);
    }).catch(() => {
      setDetail(null);
      setEvents([]);
    });
  };

  useEffect(load, [instId]);

  const submitPlan = async () => {
    if (planText.trim().length < 20) {
      toast.error("Plan is too short", { description: "Minimum 20 characters." });
      return;
    }
    setBusy(true);
    try {
      await api.post("/cap/submit", { vlk_inst_id: instId, plan_text: planText });
      toast.success("CAP submitted — awaiting compliance review");
      setPlanText("");
      load();
    } catch (e) {
      toast.error("Submission failed", { description: e?.response?.data?.detail?.code || e.message });
    } finally {
      setBusy(false);
    }
  };

  if (!instId) {
    return (
      <div className="p-6 text-sm text-muted-foreground" data-testid="institution-page-compliance">
        Session is not bound to a tenant. Contact your VeraLok administrator.
      </div>
    );
  }

  const status = detail?.status || "?";
  const isSuspended = status === "SUSPENDED";
  const canSubmit = isSuspended && (detail?.cap?.status || "NONE") !== "UNDER_REVIEW";

  return (
    <div className="space-y-6" data-testid="institution-page-compliance">
      <div>
        <h1 className="text-xl font-semibold">Compliance</h1>
        <p className="text-sm text-muted-foreground">
          Your institution's Corrective Action Plan (CAP) posture. A green badge means active with no
          open plan; a yellow badge means CAP is required; a red badge means terminally revoked.
        </p>
      </div>

      <div className="border rounded p-4" data-testid="compliance-status-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Institution</div>
            <div className="text-lg font-semibold">{instId}</div>
          </div>
          <span className={`text-xs uppercase tracking-widest px-3 py-1 rounded ${
            status === "APPROVED" ? "bg-green-500/15 text-green-700 dark:text-green-500" :
            status === "SUSPENDED" ? "bg-yellow-500/15 text-yellow-700 dark:text-yellow-500" :
            status === "REVOKED" ? "bg-destructive/10 text-destructive" : "bg-muted"
          }`} data-testid="compliance-status-badge">
            {status}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-4 text-xs">
          <Kv k="Suspended at" v={detail?.suspended_at} />
          <Kv k="CAP deadline" v={detail?.cap_deadline_at} />
          <Kv k="CAP status" v={detail?.cap?.status} />
          <Kv k="Reinstated at" v={detail?.reinstated_at} />
        </div>
      </div>

      {isSuspended && (
        <div className="border rounded p-4 space-y-3" data-testid="compliance-submit-plan">
          <div className="text-sm font-semibold">Submit Corrective Action Plan</div>
          <p className="text-xs text-muted-foreground">
            Describe the remediation steps, timeline, and owner. Compliance
            operators review submissions from the Admin Console.
          </p>
          <Textarea rows={6} placeholder="Root cause · remediation steps · target dates · accountable owner…"
                    value={planText} onChange={(e) => setPlanText(e.target.value)}
                    disabled={!canSubmit || busy}
                    data-testid="compliance-plan-textarea" />
          <Button onClick={submitPlan} disabled={!canSubmit || busy}
                  data-testid="compliance-submit-btn">
            {busy ? "Submitting…" : "Submit CAP"}
          </Button>
          {!canSubmit && detail?.cap?.status === "UNDER_REVIEW" && (
            <p className="text-xs text-muted-foreground">A CAP is already under review.</p>
          )}
        </div>
      )}

      <div className="border rounded p-4">
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          Suspension events
        </div>
        <div className="space-y-1 max-h-96 overflow-auto" data-testid="compliance-events-list">
          {events.length === 0 && <div className="text-xs text-muted-foreground">No events recorded.</div>}
          {events.map((e) => (
            <div key={e.event_id} className="text-xs border-b pb-1 mb-1 font-mono">
              <span className="text-muted-foreground">{e.created_at?.slice(0, 19)}</span>
              <span className="mx-2 uppercase font-semibold">{e.kind}</span>
              <span className="text-muted-foreground">by {e.actor_email || "system"}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Kv({ k, v }) {
  return (
    <div>
      <div className="uppercase tracking-widest text-[10px] text-muted-foreground">{k}</div>
      <div className="font-mono text-[11px]">{v || "—"}</div>
    </div>
  );
}
